import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { Loader2, Save } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Os campos que a empresa inventou, desenhados por TIPO: data vira seletor de
// data, escolha vira lista. Texto solto para tudo é o que a tela antiga fazia,
// e é o que não dava para filtrar nem exportar direito.
const CamposPersonalizados = ({ entity, entityId }) => {
	const [campos, setCampos] = useState([]);
	const [valores, setValores] = useState({});
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	const carregar = useCallback(async () => {
		if (!entityId) return;

		setCarregando(true);
		try {
			const { data } = await api.get(`/custom-fields/values/${entity}/${entityId}`);
			const lista = Array.isArray(data) ? data : [];
			setCampos(lista);
			setValores(Object.fromEntries(lista.map((c) => [c.key, c.value ?? ""])));
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [entity, entityId]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const salvar = async () => {
		setSalvando(true);
		try {
			await api.put(`/custom-fields/values/${entity}/${entityId}`, {
				values: valores,
			});
			toast.success("Campos salvos.");
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const mudar = (chave, valor) =>
		setValores((atuais) => ({ ...atuais, [chave]: valor }));

	if (carregando) {
		return (
			<div className="flex justify-center py-4">
				<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
			</div>
		);
	}

	// Sem campo definido a área some inteira: um bloco vazio em toda ficha só
	// ocuparia espaço de quem não usa o recurso.
	if (campos.length === 0) return null;

	return (
		<div className="space-y-3">
			{campos.map((campo) => (
				<div key={campo.key} className="space-y-1.5">
					<Label htmlFor={`campo-${campo.key}`}>
						{campo.label}
						{campo.required && <span className="text-destructive"> *</span>}
					</Label>

					{campo.type === "select" ? (
						<Select
							value={valores[campo.key] || "none"}
							onValueChange={(v) => mudar(campo.key, v === "none" ? "" : v)}
						>
							<SelectTrigger id={`campo-${campo.key}`}>
								<SelectValue placeholder="Escolha" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="none">—</SelectItem>
								{(campo.options || []).map((opcao) => (
									<SelectItem key={opcao} value={opcao}>
										{opcao}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : campo.type === "boolean" ? (
						<Switch
							id={`campo-${campo.key}`}
							checked={valores[campo.key] === "true"}
							onCheckedChange={(v) => mudar(campo.key, v ? "true" : "false")}
						/>
					) : (
						<Input
							id={`campo-${campo.key}`}
							// O tipo nativo do navegador em vez de máscara própria: o
							// seletor de data do celular já é melhor do que qualquer um
							// que a gente escreveria.
							type={
								campo.type === "date"
									? "date"
									: campo.type === "number"
										? "number"
										: "text"
							}
							value={valores[campo.key] || ""}
							onChange={(e) => mudar(campo.key, e.target.value)}
						/>
					)}
				</div>
			))}

			<Button size="sm" variant="outline" onClick={salvar} disabled={salvando}>
				{salvando ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Save className="h-4 w-4" />
				)}
				Salvar campos
			</Button>
		</div>
	);
};

export default CamposPersonalizados;
